/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { LookupService as CoreService } from 'app/core/services/lookup-service';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { parseCaseServiceDefaults } from 'app/cm/lib/utilities';
import { maxDecimal } from 'app/shared/lib/number-utilities';
import { naturalSort } from 'app/shared/lib/arrays';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SaveCaseService extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected caseService: CM.CaseService | null = null;

    @bindable.objectNull()
    protected defaultService: CD.Service | null = null;

    @bindable.objectNull()
    protected defaultModifier: CD.ClaimModifier | null = null;

    @bindable.objectNull()
    protected defaultPlaceOfService: CD.PlaceOfService | null = null;

    @bindable.stringNull({ changeHandler: 'reload' })
    protected taskId: string | null = null;

    @bindable.stringNull()
    protected caseId: string | null = null;

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.stringNull()
    protected editReason: string = '';

    protected maxNumber: number = Number.MAX_SAFE_INTEGER;
    protected modifiers: CD.AuthModifier[] = [];
    protected placesOfService: CD.PlaceOfService[] = [];

    protected canEditService: boolean = false;
    protected canEditModifier: boolean = false;
    protected canEditPlaceOfService: boolean = false;

    public submittedChanges: CM.CaseService | null = null;

    public serviceSearchQuery: string | null = null;          // the value of the autocomplete text box
    private serviceSearchResults: CD.Service[] = [];     // array of search results from API for autocomplete (not currently used)

    public unitSearchQuery: string | null = null;          // the value of the autocomplete text box
    private unitSearchResults: CD.Service[] = [];     // array of search results from API for autocomplete (not currently used)

    protected maxDecimal = maxDecimal;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly lookupService: LookupService,
        private readonly coreService: CoreService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadLookups();
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(!this.caseId) {
            throw new Error('Bindable property "caseId" must be bound');
        }
        if(!this.readonly) {
            this.editReason = '';
        }
        this.checkDefaults();
    }

    protected async selectService(service: CD.Service): Promise<void> {
        if(!this.caseService) { this.caseService = {} }
        this.caseService.service = service;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected async selectUnit(unit: Core.UnitOfMeasure): Promise<void> {
        if(!this.caseService) { this.caseService = {} }
        if(!this.caseService.quantity) {
            this.caseService.quantity = {};
        }
        this.caseService.quantity.unit = unit;
        this.unitSearchQuery = null;
        this.unitSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected async clearUnit(): Promise<void> {
        if(this.caseService?.quantity?.unit) this.caseService.quantity.unit = null;
    }

    protected clearService(): void {
        this.caseService!.service = null;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0);
    }

    ////****************** Functions ************************************************////
    protected serviceDisplay(service: CD.Service) {
        if(!service) return '';
        return service.code && service.description ? `${service.code} - ${service.description}` : '';
    }

    private constructSaveObject(insert: boolean = false): CM.CaseService {
        if(!this.caseId) {
            throw new Error('Bindable property "caseId" must be bound and include a case id');
        }

        // Insert does not allow null, Update uses null to clear value when empty string.
        let saveService: CM.CaseService = {
            id: this.caseService?.id,
            case: { id: this.caseId },
            service: getReference(this.caseService?.service),
            dateTimeRange: this.caseService?.dateTimeRange,
            placeOfService: getReference(this.caseService?.placeOfService),
            modifier: getReference(this.caseService?.modifier),
            charges: this.caseService?.charges,
            quantity: {
                value: this.caseService?.quantity?.value,
                unit: getReference(this.caseService?.quantity?.unit)
            },
            vendor: {
                name: { full: this.caseService?.vendor?.name?.full || (insert ? undefined : null) },
                identifier: { primary: this.caseService?.vendor?.identifier?.primary || (insert ? undefined : null) }
            }
        }

        if(this.taskId) {
            saveService.task = { id: this.taskId };
        }
        return saveService;
    }

    protected async checkDefaults(): Promise<void> {

        const defaultValues = parseCaseServiceDefaults(this.caseService?.task?.description || '');

        this.canEditService = defaultValues.serviceCode === '';
        this.canEditModifier = defaultValues.modifierCode === undefined;
        this.canEditPlaceOfService = defaultValues.placeOfServiceCode === undefined;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        if(this.caseService?.id) {
            return await this.updateCaseService();
        }
        else {
            const success = await this.insertCaseService()
            if(success) {
                this.submittedChanges = this.caseService;
            }
            return success;
        }
    }

    public reset(): void {
        this.caseService = null;
        this.serviceSearchQuery = null;
        this.serviceSearchResults.splice(0);
        this.unitSearchQuery = null;
        this.unitSearchResults.splice(0);
        this.loadCaseService();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {

        await this.doLoad(async () => {
            const placeOfServiceSorting = new Sorting();
            placeOfServiceSorting.add({
                reference: 'Description',
                direction: SortDirection.Ascending
            });
            placeOfServiceSorting.add({
                reference: 'Code',
                direction: SortDirection.Ascending
            });

            const [modifier, placeOfService] = await Promise.all(
                [
                    this.lookupService.loadClaimModifiers(undefined, { all: true }, []),
                    this.lookupService.loadPlacesOfService(undefined, { all: true, sorting: placeOfServiceSorting }, [])
                ]
            );

            this.modifiers = modifier.results;
            this.placesOfService = naturalSort(placeOfService.results, 'code');
        });
    }

    @apiCall
    private async updateCaseService(): Promise<boolean> {
        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.updateService(this.constructSaveObject(false), this.editReason);
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw (error);
        } finally {
            this.toaster.standard.saved('cm:service.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async insertCaseService(): Promise<boolean> {
        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.insertService(this.constructSaveObject(true));
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw (error);
        } finally {
            this.toaster.standard.saved('cm:service.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadCaseService(): Promise<void> {
        await this.doLoad(async () => {
            if(!this.taskId || !this.caseId) {
                return;
            }

            const service = await this.service.loadCaseService(this.caseId, this.taskId, ['service', 'modifier', 'placeOfService', 'quantity.unit']);

            if(service.results.length === 1) {
                this.caseService = service.results[0];
            }
            else {
                this.caseService = {}
                if(this.defaultService) { this.caseService.service = this.defaultService; }
                if(this.defaultModifier) { this.caseService.modifier = this.defaultModifier; }
                if(this.defaultPlaceOfService) { this.caseService.placeOfService = this.defaultPlaceOfService; }
            }
        });
    }

    @apiCall
    protected async searchServices(value: string): Promise<void> {
        await this.doLoad(async () => {
            if(!value) {
                if(this.serviceSearchResults) this.serviceSearchResults = [];
                return;
            }
            const options: Partial<IQueryLoadOptions> = { top: 30 };
            const includes: string[] = [];

            const serviceSearchFilters: IServiceSearchFilters = { isDental: false, codeOrDescription: value }; // exclude dental services and filter by code or description
            const fields: string[] = ['code', 'description'];
            const response = await this.lookupService.searchServices(undefined, options, includes, false, serviceSearchFilters, fields);
            this.serviceSearchResults = response.results;
        });
    }

    @apiCall
    protected async searchUnits(value: string): Promise<void> {
        await this.doLoad(async () => {
            if(!value) {
                if(this.unitSearchResults) this.unitSearchResults = [];
                return;
            }
            const options: Partial<IQueryLoadOptions> = { top: 30 };
            const includes: string[] = [];

            const fields: string[] = ['description'];
            const response = await this.coreService.searchUnits(value, options, includes, fields);
            this.unitSearchResults = response.results;
        });
    }
}
